import { DFPManager, DFPSlotsProvider } from '@dsch/react-dfp';

import { useLastSearch } from '../../components/PageWrappers/SharedComponents/LastSearchCard/hooks/useLastSearch';
import { useHomePage } from '../../components/HomePage/hooks/useHomePage';
import * as Styled from '../../components/HomePage/styles/HomePage.styled';
import { Hero } from '../../features/landing/Hero/Hero';
import { TopAdUnit } from '../../features/landing/TopAdUnit/TopAdUnit';
import { Sections } from '../../features/landing/Sections/Sections';
import { FeaturedAds } from '../../features/landing/FeaturedAds/FeaturedAds';
import { RecentAds } from '../../features/landing/RecentAds/RecentAds';
import { GoFurther } from '../../features/landing/GoFurther/GoFurther';
import { PopularSearches } from '../../features/landing/PopularSearches/PopularSearches';
import { HomePageValuationLookup } from '../../features/landing/HomePageValuationLookup/HomePageValuationLookup';
import { ValuationModal } from '../../components/HomePage/components/ValuationModal/ValuationModal';
import { useHomePageContext } from '../../contexts/HomePageContext';
import type { ISearchPageFilter } from '../../api/types/searchPageApiTypes';
import { LifeStyleCarousel } from '../../components/HomePage/components/MotorSearch/LifeStyleCarousel/LifeStyleCarousel';
import { LAST_SEARCH_DATA_TRACKING_VALUE } from '../../components/HomePage/helpers/constants';
import React, { useEffect } from 'react';
import { VERTICAL } from '../../helpers/verticals';
import { useVerticalContext } from '../../contexts/VerticalContext';
import { HomePageData } from './helpers';
import { pushToDataLayer } from '../../services/gtmService';
import type { DataTargetingProps } from '../../api/types/adviewApiTypes';

export interface HomePageProps {
  cdnUrl: string;
  init: HomePageData;
  domain: string;
  dfpNetworkId: string;
  unreadInAppMessage?: string;
  motorSearchFilters: ISearchPageFilter[];
}

function HomePage(props: HomePageProps) {
  const { cdnUrl, init, domain, dfpNetworkId, motorSearchFilters } = props;
  const { homePageData, status } = useHomePage({
    init,
  });
  const { vertical } = useVerticalContext();

  const { lastSearchData } = useLastSearch();

  const { isValuationModalOpen } = useHomePageContext();

  const lifestyleLinksValues = homePageData?.lifestyleLinks;

  useEffect(() => {
    if (homePageData?.dataTargetingProps) {
      pushToDataLayer(homePageData.dataTargetingProps.gtm);
    }
  }, [homePageData?.dataTargetingProps]);

  useEffect(() => {
    if (homePageData?.dataTargetingProps) {
      updateDFP(vertical, homePageData.dataTargetingProps);
    }
    // NB: The dataTargetingProps never changes as it comes from the server side
  }, [vertical, homePageData?.dataTargetingProps]);

  return (
    <Styled.HomePageWrapper>
      {/* Hero Section */}
      <Hero
        cdnUrl={cdnUrl}
        domain={domain}
        adCount={homePageData?.adCount ?? { carsTotalAdCount: 0 }}
        motorSearchFilters={motorSearchFilters}
      />
      <DFPSlotsProvider dfpNetworkId={dfpNetworkId}>
        <Styled.FlexReordering>
          {/* Top Ad Unit */}
          <TopAdUnit />
          <Styled.WhiteSection>
            <Styled.SectionBrowserHeight
              focus={vertical}
              hasSections={Boolean(homePageData?.sections.length)}
            >
              <div className="row small-collapse medium-uncollapse set-width-100">
                <div className="columns small-12" data-testid="h1-homepage">
                  <Styled.Container>
                    <Styled.StyledLastSearchCard
                      lastSearchData={lastSearchData}
                      lastSearchDataTrackingValue={
                        LAST_SEARCH_DATA_TRACKING_VALUE
                      }
                    />

                    {/* Life Style Carousel */}
                    {vertical === VERTICAL.MOTOR &&
                      lifestyleLinksValues &&
                      lifestyleLinksValues.length > 0 && (
                        <LifeStyleCarousel
                          lifestyleLinks={lifestyleLinksValues}
                        />
                      )}

                    {/* Sections */}
                    <Sections
                      status={status}
                      title={homePageData?.sectionsTitle ?? ''}
                      sections={homePageData?.sections ?? []}
                      dashLinks={homePageData?.dashLinks ?? []}
                    />
                  </Styled.Container>
                </div>
              </div>
            </Styled.SectionBrowserHeight>

            <HomePageValuationLookup />
          </Styled.WhiteSection>
        </Styled.FlexReordering>
        {/* Featured Ads */}
        {homePageData?.featuredAds && (
          <FeaturedAds
            cdnUrl={cdnUrl}
            domain={domain}
            featuredAds={homePageData.featuredAds}
          />
        )}
        {/* Recent Ads */}
        {(homePageData?.recentAds?.length ?? 0) > 0 && (
          <RecentAds
            domain={domain}
            recentAds={homePageData?.recentAds ?? []}
            recentAdsUrl={homePageData?.recentAdsUrl ?? null}
          />
        )}
        {/* Go Further */}
        <GoFurther dashLinks={homePageData?.dashLinks ?? []} />
      </DFPSlotsProvider>
      {/* Popular Searches */}
      <PopularSearches
        seoLinks={homePageData?.seoLinks ?? { internalLinks: [] }}
      />
      {isValuationModalOpen && <ValuationModal />}
    </Styled.HomePageWrapper>
  );
}

function updateDFP(vertical: VERTICAL, dataTargetingProps: DataTargetingProps) {
  DFPManager.setTargetingArguments({
    ...getDFPSettings(),
    ...dataTargetingProps?.dfp,
  });

  function getDFPSettings() {
    return {
      site: 'donedealclassifieds',
      vertical: [formatVertical()],
    };
  }

  function formatVertical() {
    return vertical.charAt(0).toUpperCase() + vertical.substring(1);
  }
}

export { HomePage };
